import { useQuery } from "@tanstack/react-query";
import { trpc } from "@/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertCircle,
  Users,
  Building2,
  Briefcase,
  CheckCircle,
  Activity,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminDashboardChart } from "./admin-dashboard-chart";
import { formatDistanceToNow } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function AdminDashboard() {
  const {
    data: providerStats,
    isLoading: isLoadingProviders,
    error: providerError,
  } = useQuery(trpc.adminDashboard.getProviderSignupStats.queryOptions());

  const {
    data: organizationStats,
    isLoading: isLoadingOrganizations,
    error: organizationError,
  } = useQuery(trpc.adminDashboard.getOrganizationStats.queryOptions());

  const {
    data: jobPostStats,
    isLoading: isLoadingJobPosts,
    error: jobPostError,
  } = useQuery(trpc.adminDashboard.getJobPostStats.queryOptions());

  const {
    data: jobDecisionStats,
    isLoading: isLoadingJobDecisions,
    error: jobDecisionError,
  } = useQuery(trpc.adminDashboard.getJobPostDecisionStats.queryOptions());

  const {
    data: activeUserCount,
    isLoading: isLoadingActiveUsers,
    error: _activeUserError,
  } = useQuery(trpc.adminDashboard.getActiveUserCount.queryOptions());

  const {
    data: providerBreakdown,
    isLoading: isLoadingProviderBreakdown,
    error: providerBreakdownError,
  } = useQuery(trpc.adminDashboard.getProviderBreakdown.queryOptions());

  const isLoading =
    isLoadingProviders ||
    isLoadingOrganizations ||
    isLoadingJobPosts ||
    isLoadingJobDecisions;
  const isLoadingBreakdown = isLoadingProviderBreakdown;
  const hasError =
    providerError || organizationError || jobPostError || jobDecisionError;
  const hasBreakdownError = providerBreakdownError;

  if (hasError) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load dashboard data. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Calculate current totals from the latest data points
  const currentProviderTotal =
    providerStats?.[providerStats.length - 1]?.totalProviders ?? 0;
  const currentOrgTotal =
    organizationStats?.[organizationStats.length - 1]?.totalOrganizations ?? 0;
  const currentJobPostTotal =
    jobPostStats?.[jobPostStats.length - 1]?.totalJobPosts ?? 0;
  const currentActiveJobPostTotal =
    jobPostStats?.[jobPostStats.length - 1]?.totalActiveJobPosts ?? 0;
  const currentJobDecisionTotal =
    jobDecisionStats?.[jobDecisionStats.length - 1]?.totalDecisions ?? 0;

  // Calculate weekly changes
  const providerWeeklyChange =
    providerStats?.[providerStats.length - 1]?.newProviders ?? 0;
  const orgWeeklyChange =
    organizationStats?.[organizationStats.length - 1]?.newOrganizations ?? 0;
  const jobPostWeeklyChange =
    jobPostStats?.[jobPostStats.length - 1]?.newJobPosts ?? 0;
  const jobDecisionWeeklyChange =
    jobDecisionStats?.[jobDecisionStats.length - 1]?.newDecisions ?? 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground">
          Platform statistics and analytics for the last 6 months
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Providers
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {currentProviderTotal.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  +{providerWeeklyChange} this week
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organizations</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {currentOrgTotal.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  +{orgWeeklyChange} this week
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Job Posts</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {currentJobPostTotal.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  {currentActiveJobPostTotal} active, +{jobPostWeeklyChange}{" "}
                  this week
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Job Decisions</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {currentJobDecisionTotal.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  +{jobDecisionWeeklyChange} this week
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoadingActiveUsers ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {(activeUserCount?.activeUsers ?? 0).toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  in the last 7 days
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Provider Signups Over Time</CardTitle>
            <p className="text-sm text-muted-foreground">
              New and total provider signups by week
            </p>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <AdminDashboardChart
                data={providerStats || []}
                dataKeys={[
                  {
                    key: "newProviders",
                    name: "New Providers",
                    color: "#8884d8",
                  },
                  {
                    key: "totalProviders",
                    name: "Total Providers",
                    color: "#82ca9d",
                  },
                ]}
                xAxisKey="week"
                height={300}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Organization Growth</CardTitle>
            <p className="text-sm text-muted-foreground">
              New organization business units over time
            </p>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <AdminDashboardChart
                data={organizationStats || []}
                dataKeys={[
                  {
                    key: "newOrganizations",
                    name: "New Organizations",
                    color: "#ffc658",
                  },
                  {
                    key: "totalOrganizations",
                    name: "Total Organizations",
                    color: "#ff7c7c",
                  },
                ]}
                xAxisKey="week"
                height={300}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Job Post Activity</CardTitle>
            <p className="text-sm text-muted-foreground">
              Job posts created and active count over time
            </p>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <AdminDashboardChart
                data={jobPostStats || []}
                dataKeys={[
                  {
                    key: "newJobPosts",
                    name: "New Job Posts",
                    color: "#8dd1e1",
                  },
                  {
                    key: "totalJobPosts",
                    name: "Total Job Posts",
                    color: "#d084d0",
                  },
                  {
                    key: "totalActiveJobPosts",
                    name: "Active Job Posts",
                    color: "#87d068",
                  },
                ]}
                xAxisKey="week"
                height={300}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Job Interest Decisions</CardTitle>
            <p className="text-sm text-muted-foreground">
              Provider interest decisions over time
            </p>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <AdminDashboardChart
                data={jobDecisionStats || []}
                dataKeys={[
                  {
                    key: "newDecisions",
                    name: "New Decisions",
                    color: "#ff8042",
                  },
                  {
                    key: "totalDecisions",
                    name: "Total Decisions",
                    color: "#00c49f",
                  },
                ]}
                xAxisKey="week"
                height={300}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Provider Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>Provider Breakdown by Specialty & State</CardTitle>
          <p className="text-sm text-muted-foreground">
            Distribution of completed providers by specialty and state license
          </p>
        </CardHeader>
        <CardContent>
          {hasBreakdownError ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load provider breakdown data.
              </AlertDescription>
            </Alert>
          ) : isLoadingBreakdown ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : providerBreakdown && providerBreakdown.length > 0 ? (
            <div className="max-h-[360px] overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">Provider Count</TableHead>
                    <TableHead>Specialty</TableHead>
                    <TableHead className="w-[120px]">State License</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providerBreakdown.map((row, index) => (
                    <TableRow
                      key={`${row.specialty}-${row.stateLicense}-${index}`}
                    >
                      <TableCell className="font-medium">
                        {row.providerCount.toLocaleString()}
                      </TableCell>
                      <TableCell>{row.specialty}</TableCell>
                      <TableCell>{row.stateLicense}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex h-[200px] items-center justify-center text-muted-foreground">
              No provider breakdown data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Update Info */}
      <div className="text-xs text-muted-foreground text-center">
        Data is updated weekly. Last update:{" "}
        {formatDistanceToNow(new Date(), { addSuffix: true })}
      </div>
    </div>
  );
}
